import axios, { type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from './store/auth.store';

function uuidv4(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const buf = new Uint8Array(16);
  crypto.getRandomValues(buf);
  buf[6] = (buf[6] & 0x0f) | 0x40;
  buf[8] = (buf[8] & 0x3f) | 0x80;
  const hex = Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Base URL del backend. El server expone TODOS los endpoints bajo el prefix
 * `/bff-admision` (ver server/src/main.ts → setGlobalPrefix). Lo sumamos acá
 * para que cada servicio sólo tenga que escribir el path "lógico" (ej.
 * `/auth/login`, `/portal/legajo/:token`) y no tenga que conocer el prefix.
 *
 * Prioridad de origen del valor:
 *  1. `window.__CONFIG__.VITE_API_BASE_URL` — inyectado en runtime por el
 *     entrypoint del contenedor nginx con la env var del pod (deploy k8s).
 *     Una sola imagen Docker sirve para todos los ambientes.
 *  2. `import.meta.env.VITE_API_BASE_URL` — inlineado por Vite en build time
 *     (fallback para desarrollo local con `npm run dev`).
 *
 * El host debe ir sin el `/bff-admision`:
 *   - local:    http://localhost:3000
 *   - test/prod: https://services-lab.apps.medicus.com.ar
 */
declare global {
  interface Window {
    __CONFIG__?: { VITE_API_BASE_URL?: string; VITE_ENVIRONMENT?: string };
  }
}

// Sacamos trailing slashes sin regex para evitar el ReDoS theorético que
// flaggea Sonar sobre `/\/+$/`. Es un loop O(n) con n = cantidad de slashes
// finales (típicamente 0-1, nunca más de un par).
function stripTrailingSlashes(s: string): string {
  let end = s.length;
  while (end > 0 && s.charCodeAt(end - 1) === 47 /* '/' */) end--;
  return s.slice(0, end);
}

const runtimeUrl =
  typeof window !== 'undefined' ? window.__CONFIG__?.VITE_API_BASE_URL : undefined;
const host = stripTrailingSlashes(runtimeUrl || import.meta.env.VITE_API_BASE_URL || '');
const baseURL = `${host}/bff-admision`;

export const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  config.headers.set('X-Trace-Id', uuidv4());

  const { token, empresaActivaId } = useAuthStore.getState();
  if (token) config.headers.set('Authorization', `Bearer ${token}`);
  // El selector global es el default. Un request que trae su propio
  // `X-Empresa-Id` (la carga de padrones, que elige la organización arriba
  // del dropzone) gana: `AxiosHeaders.set` sin flag pisa, así que hay que
  // preguntar antes. Sin esto, la organización elegida en la pantalla nunca
  // llegaba al server.
  if (empresaActivaId && !config.headers.has('X-Empresa-Id')) {
    config.headers.set('X-Empresa-Id', empresaActivaId);
  }

  return config;
});

/**
 * Endpoints del portal del titular (`/portal/...`): no usan el JWT del
 * backoffice — son anónimos, autenticados con `documento` + OTP propio.
 * Un 401 ahí significa "OTP mal" o "token de activación inválido", NUNCA
 * "el admin del backoffice perdió la sesión", así que jamás debe disparar
 * logout ni redirect a /login. Sin esta exclusión, ingresar mal el OTP
 * en la landing del titular sacaba al usuario de la pantalla.
 */
function esRequestDelPortalDelTitular(url?: string): boolean {
  if (!url) return false;
  // Incluye el wizard de activación de bis (`/portal-bis/…`, Fase 2): son
  // endpoints públicos del titular, un 401 ahí no es "se venció la sesión del
  // admin" y no debe disparar logout/redirect.
  return url.includes('/portal/') || url.includes('/portal-bis/');
}

/**
 * Refresh automático del access token. Ante un 401 (token vencido) intentamos
 * UNA vez renovar con el refresh token contra `/auth/refresh` y reintentamos el
 * request original; sólo si el refresh falla cerramos sesión. Usamos un axios
 * "pelado" (sin este interceptor) para el refresh → nunca recursa. El
 * `refreshing` compartido deduplica los 401 concurrentes en una sola llamada.
 */
let refreshing: Promise<string | null> | null = null;

async function intentarRefresh(): Promise<string | null> {
  const { refreshToken } = useAuthStore.getState();
  if (!refreshToken) return null;
  try {
    const { data } = await axios.post<{ token: string; refreshToken?: string }>(
      `${baseURL}/auth/refresh`,
      { refreshToken },
      { headers: { 'Content-Type': 'application/json' } },
    );
    useAuthStore.getState().setTokens(data.token, data.refreshToken ?? refreshToken);
    return data.token;
  } catch {
    return null;
  }
}

function cerrarSesion(): void {
  useAuthStore.getState().logout();
  if (
    window.location.pathname !== '/login' &&
    !window.location.pathname.startsWith('/activar/') &&
    // OJO: '/activar-bis/…' NO matchea startsWith('/activar/') (el guion corta).
    // Sin esto, un 401 en el wizard de bis expulsaría al titular a /login.
    !window.location.pathname.startsWith('/activar-bis/')
  ) {
    window.location.href = '/login';
  }
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const status = error?.response?.status;
    const original = error?.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const url = original?.url;

    // No-401, portal del titular (OTP), o el propio login/refresh → no tocar.
    // El 401 de /auth/login es "password mal": lo muestra la pantalla de login.
    const esAuthEndpoint = !!url && (url.includes('/auth/login') || url.includes('/auth/refresh'));
    if (status !== 401 || esRequestDelPortalDelTitular(url) || esAuthEndpoint || !original) {
      throw error;
    }

    // Token vencido: refrescar UNA vez y reintentar el request original.
    if (!original._retry) {
      original._retry = true;
      refreshing =
        refreshing ??
        intentarRefresh().finally(() => {
          refreshing = null;
        });
      const nuevo = await refreshing;
      if (nuevo) return api(original);
    }

    // Sin refresh válido (o ya reintentado) → cerramos sesión.
    cerrarSesion();
    throw error;
  },
);
